// The Billing tab's "Who pays whom" card (REQ-051/ADR-037): whether the customer is billed and whose
// AWS bill its usage lands on, in one sentence, with the month-effective schedule and — for
// `billing:write` — the form that adds an entry from a month. Switching to billed needs an active
// plan in force from that month: the form asks for one by name, and the server's own refusal
// (`rate_plan_required`, `period_closed`, `plan_not_active`, `assignment_exists`) is shown in words.
import { useState } from "preact/hooks";
import { useApi } from "@nexus/ui";
import type { ApiError } from "@nexus/ui";
import { errorId, Field } from "@nexus/ui";
import { hasErrors } from "@nexus/ui";
import { actorTitle, knownActor } from "../../ui/identity";
import { MonthPicker } from "@nexus/ui";
import { currentMonth } from "@nexus/ui";
import { Card, Chip } from "@nexus/ui";
import { ErrorDetails } from "@nexus/ui";
import {
  AWS_PAID_BY_LABELS,
  AWS_PAID_BY_OPTIONS,
  BILLING_MODE_LABELS,
  BILLING_MODE_OPTIONS,
  NOT_BILLED_MESSAGE,
  billingModeScheduleRow,
  billingModeSubmitMessage,
  compareScheduleMonth,
  currentModeLine,
  emptyBillingModeForm,
  isBilled,
  saveOrgBillingSettings,
  shortMonth,
  validateBillingModeForm,
  whoPaysWhom,
  type BillingModeFormValues,
  type OrgBillingSettings,
  type RatePlan,
  type Viewer,
} from "./model";

export interface WhoPaysWhomProps {
  orgId: string;
  customer: string;
  settings: OrgBillingSettings;
  canWrite: boolean;
  /** Active plans to bill on, by name; empty falls back to a typed plan id. */
  plans: readonly RatePlan[];
  viewer: Viewer;
  /** Told after a successful save: a `billed` entry may have assigned a plan. */
  onSaved(settings: OrgBillingSettings): void;
}

export function WhoPaysWhom({ orgId, customer, settings, canWrite, plans, viewer, onSaved }: WhoPaysWhomProps) {
  const client = useApi();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<BillingModeFormValues | null>(null);
  const [submitError, setSubmitError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  const activeForm = form ?? emptyBillingModeForm(settings, currentMonth(new Date()));
  const formErrors = validateBillingModeForm(activeForm);
  const friendlySubmitMessage = submitError ? billingModeSubmitMessage(submitError) : null;

  const submit = async () => {
    if (hasErrors(formErrors)) return;
    setBusy(true);
    setSubmitError(null);
    const result = await saveOrgBillingSettings(client, orgId, activeForm);
    setBusy(false);
    if (result.kind === "loaded") {
      onSaved(result.settings);
      setForm(null);
      setEditing(false);
    } else {
      setSubmitError(result.error);
    }
  };

  const schedule = [...settings.schedule].map((entry) => ({ entry, row: billingModeScheduleRow(entry, viewer) })).sort((a, b) => compareScheduleMonth(a.row, b.row));

  return (
    <Card
      title="Who pays whom"
      help="terms.whoPaysWhom"
      actions={
        canWrite && (
          <button type="button" class="button button-sm" aria-expanded={editing} onClick={() => setEditing((open) => !open)}>
            {editing ? "Close" : "Change"}
          </button>
        )
      }
    >
      <p class="lead-line">{whoPaysWhom(settings, customer)}</p>
      <p class="current-mode card-note">
        {currentModeLine(settings)}
        {!isBilled(settings) && (
          <>
            {" "}
            <Chip tone="grey">{NOT_BILLED_MESSAGE}</Chip>
          </>
        )}
      </p>

      {schedule.length > 0 && (
        <div class="table-scroll">
          <table class="data-table data-table-compact">
            <caption>Schedule</caption>
            <thead>
              <tr>
                <th scope="col">From</th>
                <th scope="col">Billing</th>
                <th scope="col">AWS paid by</th>
                <th scope="col">Set by</th>
              </tr>
            </thead>
            <tbody>
              {schedule.map(({ entry, row }) => (
                <tr key={row.effectiveMonth}>
                  <td>{shortMonth(row.effectiveMonth)}</td>
                  <td>{row.billingModeLabel}</td>
                  <td>{row.awsPaidByLabel}</td>
                  <td title={actorTitle(entry.createdBy, knownActor(entry, "createdBy"))}>{row.createdBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canWrite && editing && (
        <div class="subform" role="group" aria-labelledby="billing-mode-form-title">
          <h3 id="billing-mode-form-title">Change from a month</h3>
          <div class="form-grid">
            <Field id="billing-mode-month" label="From month" help="terms.effectiveMonth" error={formErrors.effectiveMonth}>
              <MonthPicker
                id="billing-mode-month"
                value={activeForm.effectiveMonth}
                onChange={(month) => setForm({ ...activeForm, effectiveMonth: month })}
              />
            </Field>
            <Field id="billing-mode-mode" label="Billing" help="terms.billingMode">
              <select
                id="billing-mode-mode"
                value={activeForm.billingMode}
                onChange={(e) => setForm({ ...activeForm, billingMode: e.currentTarget.value as BillingModeFormValues["billingMode"] })}
              >
                {BILLING_MODE_OPTIONS.map((mode) => (
                  <option key={mode} value={mode}>
                    {BILLING_MODE_LABELS[mode]}
                  </option>
                ))}
              </select>
            </Field>
            <Field id="billing-mode-paid-by" label="AWS paid by" help="terms.awsPaidBy">
              <select
                id="billing-mode-paid-by"
                value={activeForm.awsPaidBy}
                onChange={(e) => setForm({ ...activeForm, awsPaidBy: e.currentTarget.value as BillingModeFormValues["awsPaidBy"] })}
              >
                {AWS_PAID_BY_OPTIONS.map((paidBy) => (
                  <option key={paidBy} value={paidBy}>
                    {AWS_PAID_BY_LABELS[paidBy]}
                  </option>
                ))}
              </select>
            </Field>
            {activeForm.billingMode === "billed" && (
              <Field id="billing-mode-plan" label="Rate plan from that month" help="terms.planToAssign" error={formErrors.planId}>
                {plans.length > 0 ? (
                  <select
                    id="billing-mode-plan"
                    value={activeForm.planId}
                    aria-invalid={formErrors.planId ? true : undefined}
                    aria-describedby={formErrors.planId ? errorId("billing-mode-plan") : undefined}
                    onChange={(e) => setForm({ ...activeForm, planId: e.currentTarget.value })}
                  >
                    <option value="">Choose a plan…</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    id="billing-mode-plan"
                    class="input-mono"
                    placeholder="Plan id"
                    value={activeForm.planId}
                    aria-invalid={formErrors.planId ? true : undefined}
                    aria-describedby={formErrors.planId ? errorId("billing-mode-plan") : undefined}
                    onInput={(e) => setForm({ ...activeForm, planId: e.currentTarget.value })}
                  />
                )}
              </Field>
            )}
          </div>
          {submitError && (
            <div role="alert" class="banner banner-error">
              <p>{friendlySubmitMessage ?? submitError.message}</p>
              <ErrorDetails error={submitError} />
            </div>
          )}
          <div class="form-actions-start">
            <button type="button" class="button button-primary" disabled={busy || hasErrors(formErrors)} onClick={() => void submit()}>
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}
